import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { ArrowRight, Users } from 'lucide-react';
import { BallpitBackground } from '../components/BallpitBackground';
import { useAuth } from '../context/AuthContext';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: { transition: { staggerChildren: 0.15 } }
  };

  const products = [
    {
      logo: '/retro-logo.png',
      logoAlt: 'RetroCat',
      title: 'RetroCat',
      tagline: 'Sprint Retrospectives',
      description: 'Real-time collaborative boards with anonymous feedback, card reveal, emoji reactions, and action item tracking.',
      features: [
        'Anonymous cards for honest feedback',
        'Owner-controlled card reveal',
        'Emoji reactions & threaded comments',
        'Invite link — no account needed for guests',
      ],
      buttonText: 'Start Retrospective',
      buttonAction: () => navigate('/dashboard'),
      accentFrom: 'from-primary-600',
      accentTo: 'to-secondary-400',
    },
    {
      logo: '/poker-logo.png',
      logoAlt: 'PokerCat',
      title: 'PokerCat',
      tagline: 'Planning Poker',
      description: 'Story point estimation with anonymous voting, instant reveal, multi-round support, and CSV export.',
      features: [
        'Fibonacci, T-shirt sizing',
        'Votes hidden until owner reveals',
        'Per-round timer',
        'Invite link — guests welcome',
      ],
      buttonText: 'Start Poker Session',
      buttonAction: () => navigate('/dashboard'),
      accentFrom: 'from-accent-500',
      accentTo: 'to-accent-300',
    },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BallpitBackground />

      <div className="container mx-auto px-4 py-8 md:py-16 relative z-10">

        {/* Hero */}
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
          className="text-center mb-12 md:mb-20"
        >
          <motion.div variants={fadeInUp} className="flex justify-center mb-4 md:mb-6">
            <img src="/logo.png" alt="ScrumCat" className="w-28 sm:w-40 h-auto drop-shadow-lg" />
          </motion.div>

          <motion.h1
            variants={fadeInUp}
            className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 md:mb-5 leading-tight"
          >
            <span className="gradient-text">ScrumCat</span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed"
          >
            Real-time retrospective and planning poker for agile teams.
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            {user ? (
              <Button variant="primary" size="lg" onClick={() => navigate('/dashboard')} className="min-w-[200px]">
                Go to Dashboard <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            ) : (
              <>
                <Button variant="primary" size="lg" onClick={() => navigate('/login')} className="min-w-[200px]">
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="secondary" size="lg" onClick={() => navigate('/register')} className="min-w-[200px]">
                  Create Account
                </Button>
              </>
            )}
          </motion.div>
        </motion.div>

        {/* Product Cards */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-80px' }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8 mb-20 max-w-5xl mx-auto"
        >
          {products.map((product, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              whileHover={{ y: -6, transition: { duration: 0.25 } }}
            >
              <Card hover className="h-full overflow-hidden p-0 group">

                {/* Logo area */}
                <div className={`relative overflow-hidden bg-gradient-to-br ${product.accentFrom} ${product.accentTo}`}>
                  <div className="relative flex items-center justify-center py-4 px-8">
                    <motion.img
                      src={product.logo}
                      alt={product.logoAlt}
                      className="w-44 h-auto object-contain drop-shadow-2xl"
                      loading="lazy"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="p-7">
                  <div className="mb-1">
                    <span className={`text-xs font-semibold tracking-widest uppercase bg-gradient-to-r ${product.accentFrom} ${product.accentTo} bg-clip-text text-transparent`}>
                      {product.tagline}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    {product.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-5 leading-relaxed">
                    {product.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {product.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <span className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full bg-gradient-to-br ${product.accentFrom} ${product.accentTo} flex items-center justify-center`}>
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant="primary"
                    size="lg"
                    onClick={product.buttonAction}
                    className="w-full"
                  >
                    {product.buttonText}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* How It Works */}
        <motion.div
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-80px' }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: '1', title: 'Create or Join', desc: 'Start a new session or join an existing one with an invite link.' },
              { step: '2', title: 'Collaborate', desc: 'Add cards, vote, and comment with your team in real time.' },
              { step: '3', title: 'Get Results', desc: 'Define action items or review your estimation results.' },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeInUp} className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-600 to-secondary-400 text-white text-xl font-bold mb-4 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true, margin: '-80px' }}>
          <div className="rounded-3xl overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-700 to-primary-900" />
            <div className="relative z-10 px-8 py-12 md:py-16 text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">
                Take your team to the next level
              </h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Run more effective retrospectives and estimate faster with ScrumCat.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {user ? (
                  <Button variant="primary" size="lg" onClick={() => navigate('/dashboard')} className="min-w-[220px] !bg-none !bg-white !text-primary-700 hover:!bg-gray-100">
                    Go to Dashboard <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                ) : (
                  <>
                    <Button variant="primary" size="lg" onClick={() => navigate('/register')} className="min-w-[220px] !bg-none !bg-white !text-primary-700 hover:!bg-gray-100">
                      Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button variant="secondary" size="lg" onClick={() => navigate('/login')} className="min-w-[220px] !bg-white/10 !text-white !border-white/30 hover:!bg-white/20">
                      Sign In <Users className="ml-2 h-5 w-5" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};
